/**
 * CommissionPayoutsPage
 *
 * List offline payout batches for the clinic and create new batches from
 * approved-but-unpaid commission statements grouped by referrer.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useClinic } from '@/contexts/ClinicContext'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2,
  Plus,
  ChevronRight,
} from 'lucide-react'
import {
  listPayouts,
  createPayout,
  listEligibleStatements,
  CommissionStatement,
  PayoutBatch,
  PayoutBatchStatus,
  centsToDollars,
} from '@/services/commissionService'

const STATUS_COLORS: Record<
  PayoutBatchStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  draft: 'secondary',
  approved: 'outline',
  exported: 'outline',
  settled: 'default',
  voided: 'destructive',
}

const STATUS_LABELS: Record<PayoutBatchStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  exported: 'Exported',
  settled: 'Settled',
  voided: 'Voided',
}

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioner',
  clinic_admin: 'Clinic Admin',
  admin: 'Admin',
  super_admin: 'System Admin',
}

const formatUSDate = (iso?: string | null) => {
  if (!iso) return ''
  try {
    return format(parseISO(iso), 'MM/dd/yyyy')
  } catch {
    return iso
  }
}

export function CommissionPayoutsPage() {
  const { clinic, isAdmin } = useClinic()
  const clinicId = clinic?.id
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<string>('')
  const [showCreate, setShowCreate] = useState(false)
  const [selectedReferrerUserId, setSelectedReferrerUserId] =
    useState<string | null>(null)
  const [selectedStatementIds, setSelectedStatementIds] = useState<
    Set<string>
  >(new Set())
  const [notes, setNotes] = useState('')

  const { data: batchesData, isLoading } = useQuery({
    queryKey: ['commission-payouts', clinicId, statusFilter],
    queryFn: () =>
      listPayouts(clinicId!, {
        status: (statusFilter || undefined) as PayoutBatchStatus | undefined,
        limit: 50,
      }),
    enabled: !!clinicId,
  })

  const { data: eligible, isLoading: eligibleLoading } = useQuery({
    queryKey: ['payout-eligible-statements', clinicId],
    queryFn: () => listEligibleStatements(clinicId!),
    enabled: !!clinicId && showCreate,
  })

  const eligibleByReferrer = useMemo(() => {
    const map = new Map<
      string,
      {
        userId: string
        name: string
        role: string
        statements: CommissionStatement[]
      }
    >()
    for (const s of eligible || []) {
      const userId = s.referrer_user_id
      if (!userId) continue
      const name =
        s.referrer?.name ||
        (s.referrerUser
          ? `${s.referrerUser.first_name} ${s.referrerUser.last_name}`
          : userId.slice(0, 8))
      const role = s.role_snapshot || ''
      if (!map.has(userId)) {
        map.set(userId, { userId, name, role, statements: [] })
      }
      map.get(userId)!.statements.push(s)
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [eligible])

  const selectedReferrerGroup = useMemo(
    () =>
      selectedReferrerUserId
        ? eligibleByReferrer.find(g => g.userId === selectedReferrerUserId)
        : null,
    [eligibleByReferrer, selectedReferrerUserId],
  )

  const selectedTotalCents = useMemo(() => {
    if (!selectedReferrerGroup) return 0
    return selectedReferrerGroup.statements
      .filter(s => selectedStatementIds.has(s.id))
      .reduce((sum, s) => sum + (s.commission_amount_cents || 0), 0)
  }, [selectedReferrerGroup, selectedStatementIds])

  const createMutation = useMutation({
    mutationFn: () =>
      createPayout(clinicId!, {
        referrerUserId: selectedReferrerUserId!,
        statementIds: [...selectedStatementIds],
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-payouts'] })
      queryClient.invalidateQueries({
        queryKey: ['payout-eligible-statements'],
      })
      queryClient.invalidateQueries({
        queryKey: ['commission-statements'],
      })
      setShowCreate(false)
      setSelectedReferrerUserId(null)
      setSelectedStatementIds(new Set())
      setNotes('')
      toast({ title: 'Payout batch created' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not create batch',
        description: error.message,
      })
    },
  })

  const toggleStatement = (id: string) => {
    setSelectedStatementIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectReferrer = (userId: string) => {
    setSelectedReferrerUserId(userId)
    // By default select all statements for this referrer
    const group = eligibleByReferrer.find(g => g.userId === userId)
    setSelectedStatementIds(
      new Set(group ? group.statements.map(s => s.id) : []),
    )
  }

  if (!clinicId) return <div className="p-6">No clinic selected.</div>
  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You need clinic admin access to view payouts.
          </CardContent>
        </Card>
      </div>
    )
  }

  const batches = batchesData?.data || []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Referral Payouts</CardTitle>
            <CardDescription>
              Batch approved statements per referrer, export, and record
              offline settlement details.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter || 'all'}
              onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="exported">Exported</SelectItem>
                <SelectItem value="settled">Settled</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => {
                setShowCreate(true)
                setSelectedReferrerUserId(null)
                setSelectedStatementIds(new Set())
                setNotes('')
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Payout
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Statements</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground"
                    >
                      No payout batches yet.
                    </TableCell>
                  </TableRow>
                )}
                {batches.map((b: PayoutBatch) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      {b.referrer?.name ||
                        (b.referrerUser
                          ? `${b.referrerUser.first_name} ${b.referrerUser.last_name}`
                          : b.referrer_user_id.slice(0, 8))}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {(b.role_snapshot &&
                          (ROLE_LABELS[b.role_snapshot] ||
                            b.role_snapshot.replace(/_/g, ' '))) ||
                          '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatUSDate(b.period_start)} – {formatUSDate(b.period_end)}
                    </TableCell>
                    <TableCell>{b.statement_count}</TableCell>
                    <TableCell className="font-medium">
                      ${b.totalDollars || centsToDollars(b.total_amount_cents)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_COLORS[b.status]}>
                        {STATUS_LABELS[b.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {b.external_reference || '—'}
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/commissions/payouts/${b.id}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Payout Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Payout Batch</DialogTitle>
            <DialogDescription>
              Pick a referrer, then choose which of their approved statements
              to include in this batch.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Referrer</Label>
              <Select
                value={selectedReferrerUserId || ''}
                onValueChange={selectReferrer}
                disabled={eligibleLoading}
              >
                <SelectTrigger>
                  {eligibleLoading ? (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading referrers…
                    </span>
                  ) : (
                    <SelectValue placeholder="Pick a referrer with approved statements" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {!eligibleLoading && eligibleByReferrer.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No approved + unbatched statements
                    </div>
                  )}
                  {eligibleByReferrer.map(g => (
                    <SelectItem key={g.userId} value={g.userId}>
                      {g.name}
                      {g.role ? ` — ${ROLE_LABELS[g.role] || g.role}` : ''} (
                      {g.statements.length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedReferrerGroup && (
              <div className="space-y-2">
                <Label>Statements</Label>
                <div className="rounded-md border max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Referrals</TableHead>
                        <TableHead>Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReferrerGroup.statements.map(s => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedStatementIds.has(s.id)}
                              onCheckedChange={() => toggleStatement(s.id)}
                            />
                          </TableCell>
                          <TableCell>
                            {formatUSDate(s.period_start)} – {formatUSDate(s.period_end)}
                          </TableCell>
                          <TableCell>{s.referral_count}</TableCell>
                          <TableCell className="font-medium">
                            ${centsToDollars(s.commission_amount_cents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-sm text-muted-foreground">
                  Selected: {selectedStatementIds.size} statement
                  {selectedStatementIds.size === 1 ? '' : 's'} — $
                  {centsToDollars(selectedTotalCents)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Internal notes for this batch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={
                !selectedReferrerUserId ||
                selectedStatementIds.size === 0 ||
                createMutation.isPending
              }
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create Batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CommissionPayoutsPage
